import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  CheckCircle2, 
  ShoppingBag, 
  Truck, 
  Store,
  AlertTriangle, 
  ShieldCheck, 
  X, 
  ArrowRight, 
  DollarSign, 
  Clock, 
  ExternalLink, 
  Copy, 
  ShoppingCart,
  ChevronLeft,
  CreditCard,
  Lock,
  MapPin,
  Phone,
  User,
  Edit2,
  Plus,
  Minus,
  Trash2,
  Sparkles,
  Gift,
  Check,
  Loader2,
  Smartphone,
  Zap,
  Globe
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Language, NutriAgentCartItem } from '../types';
import { NutriAgentLaunchConfig, STORE_THRESHOLDS } from './NutriAgentConfigModal';
import { StoreLoginModal } from './StoreLoginModal';
import { toast } from 'sonner';
import { openStoreBrandedAppOrWeb } from '../lib/storeAppOpener';
import { NutriAgentOrderRecord } from './NutriAgentOrderDetailModal';

interface NutriAgentApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: NutriAgentLaunchConfig | null;
  lang: Language;
  theme: 'light' | 'dark';
  onOrderConfirmed?: (order: any) => void;
}

export const NutriAgentApprovalModal: React.FC<NutriAgentApprovalModalProps> = ({
  isOpen,
  onClose,
  config,
  lang,
  theme,
  onOrderConfirmed
}) => {
  // Top-level state declarations hoisted at the very beginning (TDZ safety)
  const [step, setStep] = useState<'review' | 'order' | 'paying' | 'confirmed'>('review');
  const [orderSummary, setOrderSummary] = useState<{ orderId?: string; receiptUrl?: string; error?: string } | null>(null);
  const [activeItems, setActiveItems] = useState<(NutriAgentCartItem & { productUrl?: string; icon?: string })[]>([]);
  const [fulfillmentMethod, setFulfillmentMethod] = useState<'delivery' | 'pickup'>(config?.fulfillmentMethod || 'delivery');
  const [deliveryAddress, setDeliveryAddress] = useState<string>('');
  const [deliveryNotes, setDeliveryNotes] = useState<string>('');
  const [deliverySlot, setDeliverySlot] = useState<string>('');
  const [recipientPhone, setRecipientPhone] = useState<string>('');
  const [recipientName, setRecipientName] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<'apple_pay' | 'card' | 'store_direct'>('apple_pay');
  const [applyBonuses, setApplyBonuses] = useState<boolean>(true);
  const [orderReferenceId, setOrderReferenceId] = useState<string>('');
  const [isEditingAddress, setIsEditingAddress] = useState<boolean>(false);
  const [isStoreLoginModalOpen, setIsStoreLoginModalOpen] = useState<boolean>(false);
  const [storeLoginSuccessCount, setStoreLoginSuccessCount] = useState<number>(0);
  const [availableAddresses, setAvailableAddresses] = useState<Array<{ id: string; fullAddress: string; streetLine: string; city: string; comment: string; raw: any }>>([]);
  const [isSyncingStoreProfile, setIsSyncingStoreProfile] = useState<boolean>(false);
  const [loyaltyBonusBalance, setLoyaltyBonusBalance] = useState<number>(35.00);
  const [silpoCartId, setSilpoCartId] = useState<string>('');
  const [silpoCheckoutWebLink, setSilpoCheckoutWebLink] = useState<string>('https://silpo.ua');
  const [silpoCheckoutMobileLink, setSilpoCheckoutMobileLink] = useState<string>('silpo://basket');
  const [silpoExpress, setSilpoExpress] = useState<{ isAvailable: boolean; promiseMinutes: number; price: number } | null>(null);
  const [isExpressSelected, setIsExpressSelected] = useState<boolean>(false);

  // Read bound store account info
  const rawStore = config?.targetStores?.[0] || 'silpo';
  const boundAccountData = React.useMemo(() => {
    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      return stores[rawStore.toLowerCase()] || stores[rawStore] || stores['silpo'] || null;
    } catch {
      return null;
    }
  }, [rawStore, isOpen, storeLoginSuccessCount]);

  const isSilpo = rawStore.toLowerCase().includes('silpo') || rawStore.toLowerCase().includes('сільпо') || rawStore.toLowerCase() === 'store';
  const isSilpoAppConnected = React.useMemo(() => {
    if (!isSilpo) return false;
    return true;
  }, [isSilpo]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Mock generated cart items tailored to store & budget
  const isUa = lang === 'ua';
  const isUaStore = ['atb', 'silpo', 'fozzy'].includes(rawStore.toLowerCase()) || config?.currency === 'UAH';
  const currencySymbol = config?.currencySymbol || (isUaStore ? '₴' : '$');
  
  const storeDisplayNames: Record<string, string> = {
    atb: 'АТБ',
    silpo: 'Сільпо',
    fozzy: 'Fozzy',
    instacart: 'Instacart',
    amazon_fresh: 'Amazon Fresh',
    walmart: 'Walmart Grocery'
  };
  const storeName = storeDisplayNames[rawStore.toLowerCase()] || rawStore.toUpperCase();

  const getStoreAppScheme = (rawStoreKey: string): string | null => {
    const s = rawStoreKey.toLowerCase();
    if (s === 'silpo') return 'silpo://basket';
    if (s === 'atb') return 'atbmarket://cart';
    if (s === 'fozzy') return 'fozzy://';
    if (s === 'instacart') return 'instacart://cart';
    if (s === 'walmart') return 'walmart://cart';
    if (s === 'amazon_fresh') return 'com.amazon.mobile.shopping://';
    return null;
  };

  const getStoreCheckoutUrl = (rawStoreKey: string): string => {
    const s = rawStoreKey.toLowerCase();
    if (s === 'silpo') return 'https://silpo.ua';
    if (s === 'atb') return 'https://www.atbmarket.com/cart';
    if (s === 'fozzy') return 'https://fozzy.zakaz.ua/uk/cart/';
    if (s === 'metro') return 'https://metro.zakaz.ua/uk/cart/';
    if (s === 'velmart') return 'https://velmart.zakaz.ua/uk/cart/';
    if (s === 'amazon_fresh') return 'https://www.amazon.com/gp/cart/view.html';
    if (s === 'walmart') return 'https://www.walmart.com/cart';
    if (s === 'instacart') return 'https://www.instacart.com/store/checkout';
    return 'https://silpo.ua';
  };

  // English → Ukrainian translation map for common grocery terms
  // so UA store searches produce real results instead of "not found"
  const enToUaGroceryMap: Record<string, string> = {
    'chicken breast': 'куряче філе',
    'chicken': 'курка',
    'organic chicken breast': 'куряче філе',
    'beef': 'яловичина',
    'beef liver': 'печінка яловича',
    'steak': 'стейк',
    'turkey': 'індичка',
    'salmon': 'лосось',
    'tuna': 'тунець',
    'fish': 'риба',
    'shrimp': 'креветки',
    'eggs': 'яйця',
    'farm fresh eggs': 'яйця курячі',
    'pasture-raised eggs': 'яйця курячі',
    'eggs c0': 'яйця С0',
    'milk': 'молоко',
    'almond milk': 'мигдалеве молоко',
    'unsweetened almond milk': 'мигдалеве молоко',
    'yogurt': 'йогурт',
    'kefir': 'кефір',
    'cottage cheese': 'сир кисломолочний',
    'cheese': 'сир',
    'mozzarella': 'моцарела',
    'parmesan': 'пармезан',
    'butter': 'масло вершкове',
    'oats': 'вівсяні пластівці',
    'rolled oats': 'вівсяні пластівці',
    'whole grain rolled oats': 'вівсяні пластівці',
    'oatmeal': 'вівсянка',
    'buckwheat': 'гречка',
    'rice': 'рис',
    'pasta': 'макарони',
    'bread': 'хліб',
    'lavash': 'лаваш',
    'banana': 'банани',
    'apple': 'яблука',
    'orange': 'апельсини',
    'avocado': 'авокадо',
    'berry': 'ягоди',
    'blueberry': 'чорниці',
    'strawberry': 'полуниця',
    'kiwi': 'ківі',
    'spinach': 'шпинат',
    'cucumber': 'огірки',
    'tomato': 'помідори',
    'salad': 'салат',
    'broccoli': 'броколі',
    'cabbage': 'капуста',
    'carrot': 'морква',
    'onion': 'цибуля',
    'potato': 'картопля',
    'pepper': 'перець',
    'garlic': 'часник',
    'nuts': 'горіхи',
    'almond': 'мигдаль',
    'peanut': 'арахіс',
    'cashew': "кеш'ю",
    'chia seeds': 'насіння чіа',
    'seeds': 'насіння',
    'olive oil': 'олія оливкова',
    'oil': 'олія',
    'sunflower oil': 'олія соняшникова',
    'coffee': 'кава',
    'tea': 'чай',
    'water': 'вода',
    'juice': 'сік',
    'sauerkraut': 'квашена капуста',
    'honey': 'мед',
    'sugar': 'цукор',
    'salt': 'сіль',
    'flour': 'борошно',
    'sour cream': 'сметана',
    'cream': 'вершки',
    'ham': 'шинка',
    'sausage': 'ковбаса',
    'bacon': 'бекон',
    'pork': 'свинина',
    'lemon': 'лимон',
    'lime': 'лайм',
    'grape': 'виноград',
    'pear': 'груша',
    'peach': 'персик',
    'mango': 'манго',
    'coconut': 'кокос',
    'corn': 'кукурудза',
    'mushroom': 'гриби',
    'beans': 'квасоля',
    'lentils': 'сочевиця',
    'chickpeas': 'нут',
    'tofu': 'тофу',
    'protein bar': 'протеїновий батончик',
    'protein powder': 'протеїн',
    'granola': 'гранола',
    'cereal': 'пластівці',
  };

  // Direct canonical Silpo product URLs for verified products
  const silpoDirectProductMap: Record<string, string> = {
    'куряче філе': 'https://silpo.ua/product/kuriache-file-zi-stegna-757310',
    'куряче філе органік': 'https://silpo.ua/product/kuriache-file-zi-stegna-757310',
    'chicken breast': 'https://silpo.ua/product/kuriache-file-zi-stegna-757310',
    'вівсяні пластівці': 'https://silpo.ua/product/plastivtsi-vivsiani-kolln-dribnogo-pomelu-563290',
    'rolled oats': 'https://silpo.ua/product/plastivtsi-vivsiani-kolln-dribnogo-pomelu-563290',
    'oats': 'https://silpo.ua/product/plastivtsi-vivsiani-kolln-dribnogo-pomelu-563290',
    'яйця': 'https://silpo.ua/product/yaitsia-kuriachi-sytyi-dvir-1-kategoriia-1001844',
    'яйця курячі': 'https://silpo.ua/product/yaitsia-kuriachi-sytyi-dvir-1-kategoriia-1001844',
    'яйця курячі с0': 'https://silpo.ua/product/yaitsia-kuriachi-sytyi-dvir-1-kategoriia-1001844',
    'eggs': 'https://silpo.ua/product/yaitsia-kuriachi-sytyi-dvir-1-kategoriia-1001844',
    'квасоля': 'https://silpo.ua/product/kvasolia-povna-chasha-v-tomatnomu-sousi-sterylizovana-969971',
    'квасоля в томатному соусі': 'https://silpo.ua/product/kvasolia-povna-chasha-v-tomatnomu-sousi-sterylizovana-969971',
  };

  const translateForUaStore = (itemName: string): string => {
    const lower = itemName.toLowerCase().trim();
    // Try exact match first
    if (enToUaGroceryMap[lower]) return enToUaGroceryMap[lower];
    // Try partial match — find longest matching key
    let bestMatch = '';
    let bestTranslation = '';
    for (const [en, ua] of Object.entries(enToUaGroceryMap)) {
      if (lower.includes(en) && en.length > bestMatch.length) {
        bestMatch = en;
        bestTranslation = ua;
      }
    }
    return bestTranslation || itemName;
  };

  const getProductStoreLink = (rawStoreKey: string, itemName: string): string => {
    const s = rawStoreKey.toLowerCase();
    const cleanLower = itemName.toLowerCase().replace(/\s*\([^)]*\)/g, '').trim();

    if (s === 'silpo') {
      // 1. Direct canonical product URL check
      if (silpoDirectProductMap[cleanLower]) return silpoDirectProductMap[cleanLower];
      for (const [key, url] of Object.entries(silpoDirectProductMap)) {
        if (cleanLower.includes(key) || key.includes(cleanLower)) {
          return url;
        }
      }
      // 2. Exact Silpo SPA Search route with find parameter
      let query = cleanLower.replace(/\b\d+(?:g|kg|мл|л|г|кг|oz|lb|шт|pk)\b/gi, '').trim();
      if (!/[а-яА-ЯіїєґІЇЄҐ]/.test(query)) {
        query = translateForUaStore(query);
      }
      return `https://silpo.ua/search?find=${encodeURIComponent(query)}`;
    }

    const isUaStore_ = ['atb', 'fozzy', 'metro', 'velmart'].includes(s);
    let query = itemName.replace(/\s*\([^)]*\)/g, '').replace(/\b\d+(?:g|kg|мл|л|г|кг|oz|lb|шт|pk)\b/gi, '').trim();
    // If it's a Ukrainian store but the item name looks English (no Cyrillic), translate it
    if (isUaStore_ && !/[а-яА-ЯіїєґІЇЄҐ]/.test(query)) {
      query = translateForUaStore(query);
    }
    const encoded = encodeURIComponent(query);
    
    if (s === 'atb') return `https://www.atbmarket.com/sch?q=${encoded}`;
    if (s === 'fozzy') return `https://fozzy.zakaz.ua/uk/search/?q=${encoded}`;
    if (s === 'amazon_fresh') return `https://www.amazon.com/s?k=${encoded}&i=amazonfresh`;
    if (s === 'walmart') return `https://www.walmart.com/search?q=${encoded}`;
    return `https://www.instacart.com/store/s?k=${encoded}`;
  };

  const getGroceryItemIcon = (name: string): string => {
    const n = name.toLowerCase();
    if (/філе|кур|meat|beef|chicken|індич|яловичин|свинин|м'яс|steak|cutlet|turkey/i.test(n)) return '🍗';
    if (/риб|fish|лосось|тунець|salmon|tuna|форель|хек|shrimp|кревет/i.test(n)) return '🐟';
    if (/яйц|egg/i.test(n)) return '🥚';
    if (/молок|milk|йогурт|yogurt|кефір|kefir|ряжанк/i.test(n)) return '🥛';
    if (/сир|cheese|cottage|пармезан|моцарел/i.test(n)) return '🧀';
    if (/вівсян|oat|вівс|пластів|порош|круп|гречк|buckwheat|рис|rice|макарон|паста|pasta|булгур|кіноа/i.test(n)) return '🥣';
    if (/хліб|bread|булочк|лаваш|тост|багет/i.test(n)) return '🍞';
    if (/банан|яблук|apple|banana|апельсин|orange|ягід|berry|чорниц|полуниц|ківі|avocado|авокадо/i.test(n)) return '🍎';
    if (/шпинат|spinach|огірок|cucumber|помідор|томат|tomato|салат|salad|броколі|broccoli|капуст|моркв|carrot/i.test(n)) return '🥗';
    if (/горіх|nut|мигдал|almond|арахіс|кеш'ю|насін|chia|seed/i.test(n)) return '🥜';
    if (/олі|масл|oil|butter|olive/i.test(n)) return '🫒';
    if (/кава|чай|tea|coffee|water|вод/i.test(n)) return '🧃';
    return '🛒';
  };

  const getEstimatedGroceryItemPrice = (name: string, isUaPrice: boolean): number => {
    const n = name.toLowerCase();
    if (isUaPrice) {
      if (/філе|beef|яловичин|стейк|лосось|salmon|сир тверд/i.test(n)) return 210.00;
      if (/курк|риба|тунець|сир кисломолоч|моцарел|індич/i.test(n)) return 125.00;
      if (/молок|мигдал|вівсян|олія|масло/i.test(n)) return 85.00;
      if (/яйц/i.test(n)) return 65.00;
      if (/банан|яблук|огірок|помідор|шпинат|броколі|авокадо/i.test(n)) return 70.00;
      if (/хліб|лаваш/i.test(n)) return 35.00;
      return 60.00;
    } else {
      if (/філе|beef|steak|salmon/i.test(n)) return 14.99;
      if (/chicken|fish|turkey|cheese/i.test(n)) return 8.99;
      if (/milk|almond|oats|oil/i.test(n)) return 4.99;
      if (/egg/i.test(n)) return 6.49;
      if (/banana|apple|spinach|avocado/i.test(n)) return 3.99;
      return 4.49;
    }
  };

  const getEstimatedMacros = (name: string) => {
    const n = name.toLowerCase();
    if (/філе|кур|beef|chicken|індич|яловичин|м'яс|steak|salmon|лосось|тунець/i.test(n)) return { protein: 45, carbs: 0, fat: 5 };
    if (/яйц|egg/i.test(n)) return { protein: 18, carbs: 1, fat: 14 };
    if (/сир кисломолоч|cottage|сир/i.test(n)) return { protein: 28, carbs: 4, fat: 6 };
    if (/молок|milk|йогурт|yogurt/i.test(n)) return { protein: 6, carbs: 8, fat: 4 };
    if (/вівсян|oat|гречк|рис|макарон|pasta|bread|хліб/i.test(n)) return { protein: 12, carbs: 65, fat: 4 };
    if (/горіх|nut|мигдал|авокадо|avocado|насін/i.test(n)) return { protein: 8, carbs: 10, fat: 28 };
    if (/шпинат|spinach|огірок|помідор|салат|броколі/i.test(n)) return { protein: 3, carbs: 6, fat: 0 };
    if (/банан|яблук|ягід|фрукт/i.test(n)) return { protein: 1, carbs: 25, fat: 0 };
    return { protein: 8, carbs: 15, fat: 4 };
  };

  const getItemMeasurement = (
    item: {
      name: string;
      quantity: number;
      unit?: string;
      weightGrams?: number;
      productType?: 'weight' | 'pack';
    },
    isUaLang: boolean
  ) => {
    const n = item.name.toLowerCase();
    
    // Explicit weight / pack indicators
    const isExplicitWeight = item.productType === 'weight' || (item.unit && /^(g|г|gram|грам|grams|kg|кг|oz|lb|lbs)$/i.test(item.unit));
    const isExplicitPack = item.productType === 'pack' || (item.unit && /^(pack|уп|пач|box|шт|pk|ct|can|btl|бут)$/i.test(item.unit));

    // Weight keywords (meat, poultry, fish, fresh loose produce)
    const isWeightKeyword = /філе|курятин|яловичин|стейк|фарш|м'ясо|телятин|свинин|лосось|риба|форель|тунець|хек|банан|яблук|огірок|помідор|шпинат|картопл|моркв|броколі|авокадо|печериц|chicken|breast|beef|steak|mince|meat|pork|salmon|fish|tuna|banana|apple|cucumber|tomato|spinach|potato|carrot|broccoli|avocado|mushroom/i.test(n);
    
    // Pack keywords (eggs, bread, packaged snacks, drinks, boxed flakes, etc.)
    const isPackKeyword = /яйц|egg|хліб|bread|батончик|bar|шоколад|chocolate|пляшк|bottle|консерв|чипс|чіпс|кава|чай|coffee|tea|10\s*шт|10pk|12pk|12ct/i.test(n);

    const isWeight = isExplicitWeight || (!isExplicitPack && (isWeightKeyword || (!isPackKeyword && /(?:[0-9]+|\b)(?:г|g|кг|kg)\b/i.test(n) && !/(?:шт|уп|pk|ct)\b/i.test(n))));

    if (isWeight) {
      let gramsPerUnit = item.weightGrams || 0;
      if (!gramsPerUnit) {
        const kgMatch = n.match(/(\d+(?:[.,]\d+)?)\s*(?:кг|kg)\b/i);
        if (kgMatch) {
          gramsPerUnit = Math.round(parseFloat(kgMatch[1].replace(',', '.')) * 1000);
        } else {
          const gMatch = n.match(/(\d+)\s*(?:г|g|грам|gram|grams|гр)\b/i);
          if (gMatch) {
            gramsPerUnit = parseInt(gMatch[1]);
          } else if (/2lb/i.test(n)) {
            gramsPerUnit = 907;
          } else if (/1lb/i.test(n)) {
            gramsPerUnit = 454;
          } else {
            gramsPerUnit = 500;
          }
        }
      }

      const totalGrams = gramsPerUnit * item.quantity;
      const formatted = isUaLang ? `${totalGrams} г` : `${totalGrams}g`;
      const singleUnit = isUaLang ? `${gramsPerUnit} г` : `${gramsPerUnit}g`;
      const badge = isUaLang ? 'г' : 'g';
      const unitRate = `/${singleUnit}`;

      return {
        type: 'weight' as const,
        totalGrams,
        label: formatted,
        formatted,
        singleUnit,
        badge,
        unitRate
      };
    } else {
      const q = item.quantity;
      const packWord = isUaLang ? 'уп.' : (q === 1 ? 'pack' : 'packs');
      const label = `${q} ${packWord}`;
      const badge = isUaLang ? 'уп.' : 'pack';
      const unitRate = isUaLang ? '/уп.' : '/pk';

      return {
        type: 'pack' as const,
        label,
        formatted: label,
        singleUnit: isUaLang ? '1 уп.' : '1 pack',
        badge,
        unitRate
      };
    }
  };

  const items: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = React.useMemo(() => {
    const isUaStore_ = isUaStore;
    const minDeliverySum = 400; // standard UA store delivery min

    // Verified healthy staple library
    const stapleLibrary: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = isUaStore_ ? [
      { 
        itemId: 'def-eggs', 
        name: isUa ? 'Яйця курячі Ситий двір 1 категорія (10 шт)' : 'Farm Fresh Eggs C0 (10pk)', 
        price: 76.99, 
        quantity: 1, 
        macros: { protein: 26, carbs: 1, fat: 20 }, 
        icon: '🥚', 
        productType: 'pack',
        productUrl: getProductStoreLink(rawStore, 'яйця курячі')
      },
      { 
        itemId: 'def-oats', 
        name: isUa ? 'Пластівці вівсяні Kolln (500г)' : 'Whole Grain Rolled Oats 500g', 
        price: 48.50, 
        quantity: 1, 
        macros: { protein: 12, carbs: 68, fat: 6 }, 
        icon: '🥣', 
        productType: 'pack',
        weightGrams: 500,
        productUrl: getProductStoreLink(rawStore, 'вівсяні пластівці')
      },
      { 
        itemId: 'def-yogurt', 
        name: isUa ? 'Грецький йогурт (300г)' : 'Greek Yogurt (300g)', 
        price: 54.00, 
        quantity: 1, 
        macros: { protein: 18, carbs: 6, fat: 0 }, 
        icon: '🥛', 
        productType: 'pack',
        weightGrams: 300,
        productUrl: getProductStoreLink(rawStore, 'грецький йогурт')
      },
      { 
        itemId: 'def-cottage', 
        name: isUa ? 'Сир кисломолочний 5% (300г)' : 'Cottage Cheese 5% (300g)', 
        price: 65.00, 
        quantity: 1, 
        macros: { protein: 28, carbs: 4, fat: 5 }, 
        icon: '🧀', 
        productType: 'pack',
        weightGrams: 300,
        productUrl: getProductStoreLink(rawStore, 'сир кисломолочний')
      },
      { 
        itemId: 'def-chicken', 
        name: isUa ? 'Куряче філе зі стегна (1кг)' : 'Organic Chicken Breast 1kg', 
        price: 215.00, 
        quantity: 1, 
        macros: { protein: 62, carbs: 0, fat: 5 }, 
        icon: '🍗', 
        productType: 'weight',
        weightGrams: 1000,
        productUrl: getProductStoreLink(rawStore, 'куряче філе')
      },
    ] : [
      { itemId: 'def-eggs', name: 'Pasture-Raised Eggs 12pk', price: 6.99, quantity: 1, macros: { protein: 72, carbs: 2, fat: 60 }, icon: '🥚', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'eggs') },
      { itemId: 'def-oats', name: 'Whole Grain Rolled Oats 32oz', price: 4.49, quantity: 1, macros: { protein: 10, carbs: 54, fat: 5 }, icon: '🥣', productType: 'pack', weightGrams: 900, productUrl: getProductStoreLink(rawStore, 'rolled oats') },
      { itemId: 'def-water', name: 'Mineral Water 1L', price: 3.99, quantity: 1, macros: { protein: 0, carbs: 0, fat: 0 }, icon: '💧', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'mineral water') },
      { itemId: 'def-chicken', name: 'Organic Chicken Breast 2lb', price: 14.99, quantity: 1, macros: { protein: 54, carbs: 0, fat: 4 }, icon: '🍗', productType: 'weight', weightGrams: 907, productUrl: getProductStoreLink(rawStore, 'chicken breast') },
    ];

    const customGroceryItems = (config?.groceryItems || []).map((gi, idx) => {
      const parsedQty = gi.amount ? Math.max(1, parseInt(gi.amount) || 1) : 1;
      const itemName = gi.amount && gi.unit ? `${gi.name} (${gi.amount}${gi.unit})` : gi.name;
      const isWeight = /^(g|г|gram|грам|grams|kg|кг|oz|lb|lbs)$/i.test(gi.unit || '') || /філе|курятин|яловичин|стейк|фарш|м'ясо|телятин|свинин|лосось|риба|форель|тунець|хек|банан|яблук|огірок|помідор|шпинат|картопл|моркв|броколі|авокадо|печериц|chicken|breast|beef|steak|mince|meat|pork|salmon|fish|tuna|banana|apple|cucumber|tomato|spinach|potato|carrot|broccoli|avocado|mushroom/i.test(gi.name);
      return {
        itemId: gi.id || `groc-${idx + 1}`,
        name: itemName,
        price: getEstimatedGroceryItemPrice(gi.name, isUaStore_),
        quantity: parsedQty,
        macros: getEstimatedMacros(gi.name),
        icon: getGroceryItemIcon(gi.name),
        productUrl: getProductStoreLink(rawStore, gi.name),
        productType: (isWeight ? 'weight' : 'pack') as 'weight' | 'pack',
        unit: gi.unit
      };
    });

    if (config?.groceryListMode === 'only_grocery') {
      return customGroceryItems.length > 0 ? customGroceryItems : stapleLibrary.slice(0, 3);
    }

    if (config?.groceryListMode === 'include_grocery' && customGroceryItems.length > 0) {
      const customSum = customGroceryItems.reduce((acc, it) => acc + it.price * it.quantity, 0);
      
      // If user's items already meet the minimum delivery threshold, do not inject massive unrequested items
      if (customSum >= minDeliverySum) {
        return customGroceryItems;
      }

      // Calculate the exact deficit to reach minimum delivery (e.g. 70 UAH)
      const deficit = minDeliverySum - customSum;
      
      // Select minimal staple items to cover deficit with minimum overshoot
      const addedStaples: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = [];
      let covered = 0;
      for (const staple of stapleLibrary) {
        if (covered < deficit) {
          addedStaples.push(staple);
          covered += staple.price * staple.quantity;
        }
      }
      return [...customGroceryItems, ...addedStaples];
    }

    // Default 'all_ai' mode: scale items to budget
    const targetSum = config?.budgetLimitUSD || (isUaStore_ ? 400 : 35);
    const aiItems: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = [];
    let acc = 0;
    for (const staple of stapleLibrary) {
      if (acc + staple.price * staple.quantity <= targetSum || aiItems.length === 0) {
        aiItems.push(staple);
        acc += staple.price * staple.quantity;
      }
    }
    return aiItems.length > 0 ? aiItems : stapleLibrary.slice(0, 3);
  }, [config, isUaStore, isUa, rawStore]);

  React.useEffect(() => {
    if (isOpen) {
      if (items && items.length > 0) {
        setActiveItems(items);
      }
      if (config?.fulfillmentMethod) {
        setFulfillmentMethod(config.fulfillmentMethod);
      }
      const prefix = (rawStore || 'silpo').toUpperCase();
      setOrderReferenceId(`${prefix}-ORD-${Math.floor(100000 + Math.random() * 900000)}`);
      
      const savedUserStr = localStorage.getItem('nutri_agent_user');
      let parsedUser: any = null;
      try { if (savedUserStr) parsedUser = JSON.parse(savedUserStr); } catch {}

      const storedUserName = localStorage.getItem('userName') || '';
      const initialName = boundAccountData?.name || parsedUser?.name || storedUserName || '';
      setRecipientName(initialName);

      const initialPhone = boundAccountData?.phone || parsedUser?.phone || (isUaStore ? '+380 63 498 55 59' : '+1 (555) 234-5678');
      setRecipientPhone(initialPhone);

      const initialAddr = boundAccountData?.address || parsedUser?.address || '';
      setDeliveryAddress(initialAddr);
      setDeliveryNotes(boundAccountData?.notes || parsedUser?.notes || '');
      setDeliverySlot(isUa ? 'Сьогодні, 19:30 – 20:30 (Найближчий час)' : 'Today, 7:30 PM – 8:30 PM');
      setStep('review');

      // Sync real profile, delivery addresses and loyalty from connected Silpo app
      if (rawStore.toLowerCase() === 'silpo') {
        setIsSyncingStoreProfile(true);
        fetch('/api/nutri-agent/silpo-profile')
          .then(r => r.json())
          .then(res => {
            setIsSyncingStoreProfile(false);
            if (res.success) {
              if (res.addresses && res.addresses.length > 0) {
                setAvailableAddresses(res.addresses);
              }
              if (!initialAddr && res.primaryAddress) {
                setDeliveryAddress(res.primaryAddress);
              }
              if (!boundAccountData?.notes && res.primaryNotes) {
                setDeliveryNotes(res.primaryNotes);
              }
              if (res.phone) {
                setRecipientPhone(res.phone);
              }
              if (res.name && !initialName) {
                setRecipientName(res.name);
              }
              if (res.loyaltyBalance !== null && res.loyaltyBalance !== undefined) {
                setLoyaltyBonusBalance(res.loyaltyBalance);
              }

              // Persist back to store credentials so local cache is updated
              try {
                const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
                const existing = stores['silpo'] || {};
                stores['silpo'] = {
                  ...existing,
                  connected: true,
                  phone: res.phone || existing.phone,
                  cardId: res.loyaltyCard || existing.cardId,
                  address: res.primaryAddress || existing.address,
                  notes: res.primaryNotes || existing.notes,
                  name: res.name || existing.name || initialName,
                  loyaltyBalance: res.loyaltyBalance ?? existing.loyaltyBalance,
                  connectedAt: existing.connectedAt || Date.now()
                };
                localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
              } catch (e) {}
            }
          })
          .catch(err => {
            setIsSyncingStoreProfile(false);
            console.warn('[NutriAgentApprovalModal] Silpo profile sync error:', err);
          });

        // Also initiate background cart pre-sync for real stock, timeslot validation, and express delivery
        const cartSyncPayload = (items && items.length > 0) ? items : activeItems;
        fetch('/api/nutri-agent/sync-silpo-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartSyncPayload })
        })
          .then(r => r.json())
          .then(res => {
            if (res.success) {
              if (res.shoppingCartId) setSilpoCartId(res.shoppingCartId);
              if (res.checkoutWebLink) setSilpoCheckoutWebLink(res.checkoutWebLink);
              if (res.checkoutMobileLink) setSilpoCheckoutMobileLink(res.checkoutMobileLink);
              if (res.expressDelivery) setSilpoExpress(res.expressDelivery);
              if (res.loyalty?.bonusAvailable) setLoyaltyBonusBalance(res.loyalty.bonusAvailable);
            }
          })
          .catch(err => console.warn('[NutriAgentApprovalModal] Silpo initial cart sync warning:', err));
      }
    }
  }, [isOpen, config, isUaStore, isUa, rawStore, boundAccountData, items]);

  const displayedItems = activeItems.length > 0 ? activeItems : items;
  const subtotal = displayedItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  
  const isPickup = fulfillmentMethod === 'pickup';
  const storeThreshold = STORE_THRESHOLDS[rawStore.toLowerCase()] || { 
    minDeliverySum: isUaStore ? 400 : 35, 
    freeDeliveryThreshold: isUaStore ? 1500 : 75,
    deliveryFee: isUaStore ? 50 : 5.99, 
    pickupAvailable: true 
  };
  const baseDeliveryFee = isPickup ? 0 : (isUaStore ? (storeThreshold.deliveryFee || 50.00) : (storeThreshold.deliveryFee || 5.99));
  const freeThreshold = storeThreshold.freeDeliveryThreshold || (isUaStore ? 1000 : 75);
  const standardDeliveryFee = isPickup ? 0 : (subtotal >= freeThreshold ? 0 : baseDeliveryFee);
  const deliveryFee = (isExpressSelected && silpoExpress?.isAvailable) ? silpoExpress.price : standardDeliveryFee;
  const isBelowMinDelivery = !isPickup && subtotal < storeThreshold.minDeliverySum;
  const deficitToMin = Math.max(0, parseFloat((storeThreshold.minDeliverySum - subtotal).toFixed(2)));
  const progressPercent = Math.min(100, Math.round((subtotal / storeThreshold.minDeliverySum) * 100));

  const bonusDiscount = (applyBonuses && isUaStore && subtotal > 50) ? Math.min(loyaltyBonusBalance, subtotal) : 0;
  const totalCost = Math.max(0, subtotal + deliveryFee - bonusDiscount);
  const totalProtein = displayedItems.reduce((acc, item) => acc + item.macros.protein * item.quantity, 0);
  const totalCalories = displayedItems.reduce((acc, item) => acc + ((item.macros.protein * 4) + (item.macros.carbs * 4) + (item.macros.fat * 9)) * item.quantity, 0);

  const handleAutoTopUp = () => {
    const currentSum = displayedItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const deficit = Math.max(0, storeThreshold.minDeliverySum - currentSum);
    if (deficit <= 0) {
      toast.info(isUa ? 'Мінімальну суму доставки вже досягнуто!' : 'Delivery minimum already met!');
      return;
    }

    const stapleCandidates: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = isUaStore ? [
      { itemId: `topup-eggs-${Date.now()}`, name: 'Яйця курячі Ситий двір 1 категорія (10 шт)', price: 76.99, quantity: 1, macros: { protein: 26, carbs: 1, fat: 20 }, icon: '🥚', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'яйця курячі') },
      { itemId: `topup-cottage-${Date.now()}`, name: 'Сир кисломолочний 5% (300г)', price: 65.00, quantity: 1, macros: { protein: 28, carbs: 4, fat: 5 }, icon: '🧀', productType: 'pack', weightGrams: 300, productUrl: getProductStoreLink(rawStore, 'сир кисломолочний') },
      { itemId: `topup-yogurt-${Date.now()}`, name: 'Грецький йогурт (300г)', price: 54.00, quantity: 1, macros: { protein: 18, carbs: 6, fat: 0 }, icon: '🥛', productType: 'pack', weightGrams: 300, productUrl: getProductStoreLink(rawStore, 'грецький йогурт') },
      { itemId: `topup-oats-${Date.now()}`, name: 'Пластівці вівсяні Kolln (500г)', price: 48.50, quantity: 1, macros: { protein: 12, carbs: 68, fat: 6 }, icon: '🥣', productType: 'pack', weightGrams: 500, productUrl: getProductStoreLink(rawStore, 'вівсяні пластівці') },
      { itemId: `topup-water-${Date.now()}`, name: 'Вода мінеральна Borjomi (0.5л)', price: 42.00, quantity: 1, macros: { protein: 0, carbs: 0, fat: 0 }, icon: '💧', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'вода') }
    ] : [
      { itemId: `topup-eggs-${Date.now()}`, name: 'Pasture-Raised Large Eggs (12ct)', price: 6.99, quantity: 1, macros: { protein: 72, carbs: 4, fat: 60 }, icon: '🥚', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'eggs') },
      { itemId: `topup-oats-${Date.now()}`, name: 'Organic Rolled Oats (32oz)', price: 4.49, quantity: 1, macros: { protein: 30, carbs: 140, fat: 14 }, icon: '🥣', productType: 'pack', weightGrams: 900, productUrl: getProductStoreLink(rawStore, 'rolled oats') },
      { itemId: `topup-water-${Date.now()}`, name: 'Electrolyte Mineral Water (1L)', price: 3.99, quantity: 1, macros: { protein: 0, carbs: 0, fat: 0 }, icon: '💧', productType: 'pack', productUrl: getProductStoreLink(rawStore, 'mineral water') }
    ];

    const toAdd: (NutriAgentCartItem & { productUrl?: string; icon?: string })[] = [];
    let covered = 0;
    let idx = 0;
    while (covered < deficit && idx < 30) {
      const candidate = stapleCandidates[idx % stapleCandidates.length];
      const existingInToAdd = toAdd.find(i => i.name === candidate.name);
      if (existingInToAdd) {
        existingInToAdd.quantity += 1;
      } else {
        toAdd.push({
          ...candidate,
          itemId: `topup-${candidate.itemId || idx}-${Date.now()}-${idx}`
        });
      }
      covered += candidate.price;
      idx++;
    }

    setActiveItems(prev => {
      const current = prev.length > 0 ? prev : items;
      const next = current.map(item => ({ ...item }));
      for (const add of toAdd) {
        const match = next.find(i => i.name === add.name);
        if (match) {
          match.quantity += add.quantity;
        } else {
          next.push({ ...add });
        }
      }
      return next;
    });
    const addedTotal = toAdd.reduce((a, b) => a + b.price * b.quantity, 0);
    toast.success(isUa ? `⚡ Додано дефіцитні товари (+${currencySymbol}${addedTotal.toFixed(2)}) до мінімуму доставки!` : `⚡ Added deficit items (+${currencySymbol}${addedTotal.toFixed(2)}) to reach delivery minimum!`);
  };

  const handleUpdateQty = (itemId: string, delta: number) => {
    setActiveItems(prev => {
      const current = prev.length > 0 ? prev : items;
      return current.map(item => {
        if (item.itemId === itemId) {
          const newQty = Math.max(0, item.quantity + delta);
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(item => item.quantity > 0);
    });
  };

  const handleRemoveItem = (itemId: string) => {
    setActiveItems(prev => {
      const current = prev.length > 0 ? prev : items;
      return current.filter(item => item.itemId !== itemId);
    });
  };

  const handleOpenStoreAppOrWeb = (customUrl?: string, action?: 'checkout' | 'cart' | 'product' | 'search', searchQuery?: string, forceWeb?: boolean) => {
    const targetCheckoutUrl = customUrl || orderSummary?.receiptUrl || (isSilpo ? (silpoCheckoutWebLink && !silpoCheckoutWebLink.endsWith('/checkout') ? silpoCheckoutWebLink : 'https://silpo.ua') : getStoreCheckoutUrl(rawStore));
    openStoreBrandedAppOrWeb({
      store: rawStore,
      webUrl: targetCheckoutUrl,
      action: action || 'checkout',
      searchQuery: searchQuery,
      forceWeb: forceWeb
    });
  };

  const handleProceedToPayment = async () => {
    const targetCheckoutUrl = isSilpo 
      ? (silpoCheckoutWebLink && !silpoCheckoutWebLink.endsWith('/checkout') ? silpoCheckoutWebLink : 'https://silpo.ua') 
      : getStoreCheckoutUrl(rawStore);

    const newOrder: NutriAgentOrderRecord = {
      id: orderReferenceId || `ORD-${Math.floor(100000 + Math.random() * 900000)}`,
      store: storeName,
      rawStore: rawStore,
      total: totalCost,
      subtotal: subtotal,
      deliveryFee: deliveryFee,
      discount: bonusDiscount,
      items: displayedItems.length,
      itemDetails: displayedItems.map(it => ({ 
        name: it.name, 
        qty: it.quantity, 
        price: it.price,
        icon: it.icon,
        productUrl: it.productUrl,
        macros: it.macros,
        productType: it.productType,
        unit: it.unit,
        weightGrams: it.weightGrams
      })),
      deliveryAddress: deliveryAddress,
      deliveryNotes: deliveryNotes,
      deliverySlot: deliverySlot,
      recipientName: recipientName,
      recipientPhone: recipientPhone,
      paymentMethod: paymentMethod,
      timestamp: Date.now(),
      currencySymbol: currencySymbol,
      currency: config.currency,
      status: 'PAID_AND_DISPATCHED',
      checkoutUrl: targetCheckoutUrl,
      isExpress: isExpressSelected
    };

    if (isSilpo) {
      setStep('paying');
      try {
        const syncRes = await fetch('/api/nutri-agent/sync-silpo-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: displayedItems })
        }).then(r => r.json());

        const cartId = syncRes?.shoppingCartId || silpoCartId;
        if (cartId) {
          setSilpoCartId(cartId);
          if (applyBonuses && bonusDiscount > 0) {
            fetch('/api/nutri-agent/apply-silpo-bonuses', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ shoppingCartId: cartId, bonusRequested: bonusDiscount })
            }).catch(() => {});
          }
          if (isExpressSelected) {
            fetch('/api/nutri-agent/switch-silpo-express', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ shoppingCartId: cartId })
            }).catch(() => {});
          }
        }
        if (syncRes?.checkoutWebLink && !syncRes.checkoutWebLink.endsWith('/checkout')) {
          setSilpoCheckoutWebLink(syncRes.checkoutWebLink);
        }
        if (syncRes?.checkoutMobileLink) {
          setSilpoCheckoutMobileLink(syncRes.checkoutMobileLink);
        }
      } catch (err) {
        console.warn('[Silpo Cart Sync error]', err);
      }
    }

    // Dispatch store app launcher
    openStoreBrandedAppOrWeb({
      store: rawStore,
      webUrl: targetCheckoutUrl,
      action: 'checkout',
      forceWeb: false
    });

    toast.success(
      isSilpo
        ? (isUa 
            ? 'Кошик Сільпо заповнено! Відкриваємо застосунок 📱🛒' 
            : 'Silpo cart populated! Opening app 📱🛒')
        : (isUa 
            ? `Переходимо до оплати на ${storeName} 🛒🔒` 
            : `Proceeding to payment on ${storeName} 🛒🔒`),
      { duration: 5000 }
    );

    setOrderSummary({ orderId: newOrder.id, receiptUrl: targetCheckoutUrl });
    setStep('confirmed');
    if (onOrderConfirmed) onOrderConfirmed(newOrder);
  };

  const handleCopyList = () => {
    const header = isUa 
      ? `🛒 Замовлення Nutri-Agent (${storeName}) — #${orderReferenceId || 'NEW'}:\n` 
      : `🛒 Nutri-Agent Order (${storeName}) — #${orderReferenceId || 'NEW'}:\n`;
    
    const lines = displayedItems.map((it, idx) => 
      `${idx + 1}. ${it.name} (x${it.quantity}) — ${currencySymbol}${(it.price * it.quantity).toFixed(2)}\n   🔗 ${it.productUrl}`
    ).join('\n');

    const totalText = isPickup
      ? (isUa 
          ? `\n🏬 Спосіб: Самовивіз (${deliveryAddress})\n⏰ Слот: ${deliverySlot}\n💰 Разом до сплати: ~${currencySymbol}${totalCost.toFixed(2)} (безкоштовний самовивіз)` 
          : `\n🏬 Method: Store Pickup (${deliveryAddress})\n⏰ Slot: ${deliverySlot}\n💰 Total to pay: ~${currencySymbol}${totalCost.toFixed(2)} (free pickup)`)
      : (isUa 
          ? `\n🚚 Спосіб: Доставка кур'єром\n📍 Адреса: ${deliveryAddress}\n⏰ Слот: ${deliverySlot}\n💰 Разом до сплати: ~${currencySymbol}${totalCost.toFixed(2)} (з доставкою)` 
          : `\n🚚 Method: Courier Delivery\n📍 Address: ${deliveryAddress}\n⏰ Slot: ${deliverySlot}\n💰 Total to pay: ~${currencySymbol}${totalCost.toFixed(2)} (with delivery)`);

    navigator.clipboard.writeText(`${header}${lines}${totalText}`).then(() => {
      toast.success(isUa ? 'Деталі замовлення скопійовано!' : 'Order details copied to clipboard!', { icon: '📋' });
    });
  };

  // Tone Engine
  const copyMessage = isUa
    ? `Nutri-Agent підібрав ${displayedItems.length} оптимальних позицій у магазині ${storeName}. Набрано ${totalProtein}g чистого білка при повному дотриманні дієтичних обмежень SafeChoice! Ви можете перейти до замовлення на сайті магазину з безпечною оплатою через Apple Pay / Google Pay / карткою.`
    : `Nutri-Agent selected ${displayedItems.length} optimal items at ${storeName}. Total protein: ${totalProtein}g with 100% adherence to your dietary restrictions. Proceed to checkout on the official store website with secure Apple Pay / Google Pay / Card payment.`;

  return (
    <>
      <AnimatePresence>
        {isOpen && config && (
        <motion.div
          key="approval-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            key="approval-modal-card"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "relative w-full max-w-xl overflow-hidden rounded-3xl",
              "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50",
              "border border-zinc-200 dark:border-zinc-800 shadow-2xl flex flex-col max-h-[90vh]"
            )}
          >
          {/* Top Header */}
          <div className="p-5 pb-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between shrink-0 bg-white dark:bg-zinc-950">
            <div className="flex items-center gap-3">
              {step === 'order' ? (
                <button
                  onClick={() => setStep('review')}
                  className="w-9 h-9 rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-emerald-500/50 flex items-center justify-center text-zinc-600 dark:text-zinc-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors cursor-pointer"
                  title={isUa ? 'Назад до кошика' : 'Back to Cart'}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              ) : (
                <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 text-emerald-500">
                  <ShoppingBag className="w-5 h-5" />
                </div>
              )}
              <div>
                <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-50 tracking-tight flex items-center gap-2">
                  {step === 'confirmed' && (isUa ? 'Замовлення Сформовано!' : 'Order Confirmed!')}
                  {step === 'paying' && (isUa ? 'Платіжний Шлюз...' : 'Processing Payment...')}
                  {step === 'order' && (isUa ? 'Огляд замовлення' : 'Order Review')}
                  {step === 'review' && (isUa ? 'Авто-Кошик Nutri-Agent' : 'Nutri-Agent Cart Review')}
                </h2>
                <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                  <span>{storeName}</span>
                  <span>•</span>
                  <span>
                    {step === 'order' 
                      ? (isUa ? 'ОГЛЯД ЗАМОВЛЕННЯ ПЕРЕД ОПЛАТОЮ' : 'ORDER REVIEW BEFORE PAYMENT')
                      : (step === 'confirmed' 
                        ? (isUa ? 'УСПІШНО ОФОРМЛЕНО' : 'SUCCESSFULLY PLACED') 
                        : (step === 'paying' ? 'PCI-DSS 256-BIT ENCRYPTION' : 'DIRECT STORE TRANSFER'))}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Stepper progress indicator */}
          <div className="px-5 py-2.5 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between text-xs font-sans shrink-0">
            <div className={cn(
              "flex items-center gap-1.5",
              step === 'review' ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-zinc-400 dark:text-zinc-500 font-medium"
            )}>
              <span className={cn(
                "w-4 h-4 rounded-full flex items-center justify-center text-[9px]",
                step === 'review' ? "bg-emerald-500 text-white font-bold" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold"
              )}>
                {step !== 'review' ? '✓' : '1'}
              </span>
              <span>{isUa ? '1. Кошик' : '1. Cart'}</span>
            </div>
            <span className="text-zinc-300 dark:text-zinc-700">→</span>
            <div className={cn(
              "flex items-center gap-1.5",
              step === 'order' ? "text-emerald-600 dark:text-emerald-400 font-bold" : (step === 'paying' || step === 'confirmed' ? "text-zinc-700 dark:text-zinc-300 font-medium" : "text-zinc-400 dark:text-zinc-500 font-medium")
            )}>
              <span className={cn(
                "w-4 h-4 rounded-full flex items-center justify-center text-[9px]",
                step === 'order' ? "bg-emerald-500 text-white font-bold animate-pulse" : (step === 'paying' || step === 'confirmed' ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold" : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400")
              )}>
                {step === 'paying' || step === 'confirmed' ? '✓' : '2'}
              </span>
              <span>{isUa ? '2. Огляд замовлення' : '2. Order review'}</span>
            </div>
            <span className="text-zinc-300 dark:text-zinc-700">→</span>
            <div className={cn(
              "flex items-center gap-1.5",
              step === 'paying' || step === 'confirmed' ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-zinc-400 dark:text-zinc-500 font-medium"
            )}>
              <span className={cn(
                "w-4 h-4 rounded-full flex items-center justify-center text-[9px]",
                step === 'confirmed' ? "bg-emerald-500 text-white font-bold" : (step === 'paying' ? "bg-emerald-500 text-white animate-spin" : "bg-zinc-200 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400")
              )}>
                {step === 'confirmed' ? '✓' : '3'}
              </span>
              <span>{isUa ? '3. Оплата' : '3. Payment'}</span>
            </div>
          </div>

          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {step === 'confirmed' && (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center mx-auto text-emerald-500">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">
                    {isUa ? 'Замовлення успішно підтверджено!' : 'Order Placed & Transferred!'}
                  </h3>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-bold">
                    Order Ref: {orderSummary?.orderId || orderReferenceId}
                  </p>
                </div>
                
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed max-w-md mx-auto">
                  {isUa 
                    ? `Товари та параметри доставки успішно передано до ${storeName}. Відкрито офіційну сторінку для перевірки кошика, відстеження та здійснення оплати.`
                    : `Order details and delivery preferences successfully dispatched to ${storeName}. Official cart & payment portal opened in your browser or app.`}
                </p>

                <div className="bg-zinc-50 dark:bg-zinc-900/60 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 text-left space-y-2.5 text-xs text-zinc-700 dark:text-zinc-300">
                  <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                    <span className="flex items-center gap-2">
                      {isPickup ? <Store className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                      {isPickup 
                        ? (isUa ? `Самовивіз із магазину ${storeName}` : `${storeName} Store Pickup`) 
                        : (isUa ? `Доставка кур'єром ${storeName}` : `${storeName} Courier Delivery`)}
                    </span>
                    <span>{deliverySlot}</span>
                  </div>
                  <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-400">
                    <span className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-emerald-500" />
                      {isPickup ? (isUa ? 'Пункт видачі:' : 'Pickup Point:') : (isUa ? 'Адреса доставки:' : 'Destination:')}
                    </span>
                    <span className="truncate max-w-[200px] text-zinc-900 dark:text-zinc-100 font-medium">{deliveryAddress}</span>
                  </div>
                  <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-400">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-emerald-500" />
                      {isUa ? 'Сума до сплати:' : 'Total Amount:'}
                    </span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{currencySymbol}{totalCost.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  {isSilpo ? (
                    <>
                      <div className="bg-emerald-500/10 dark:bg-emerald-500/15 p-3.5 rounded-2xl border border-emerald-500/30 text-left text-xs space-y-1">
                        <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-bold text-[11px]">
                          <Smartphone className="w-4 h-4 text-emerald-500 shrink-0" />
                          <span>
                            {boundAccountData?.phone || boundAccountData?.cardId || boundAccountData?.email
                              ? (isUa ? `Додаток Сільпо підключено (${boundAccountData.phone || boundAccountData.cardId || boundAccountData.email})` : `Silpo app connected (${boundAccountData.phone || boundAccountData.cardId || boundAccountData.email})`)
                              : (isUa ? 'Замовлення сформовано для застосунку Сільпо 📱' : 'Order ready for Silpo App 📱')}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                          {isUa 
                            ? 'Замовлення синхронізовано з кошиком Сільпо. Натисніть кнопку нижче, щоб завершити замовлення у додатку (або на сайті silpo.ua у браузері).' 
                            : 'Order synced with Silpo cart. Tap below to complete checkout in the app (or on silpo.ua website).'}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          openStoreBrandedAppOrWeb({
                            store: 'silpo',
                            webUrl: silpoCheckoutWebLink || 'https://silpo.ua',
                            action: 'checkout',
                            forceWeb: false
                          });
                        }}
                        className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md shadow-emerald-500/20 uppercase tracking-wider"
                      >
                        <Smartphone className="w-4 h-4" />
                        {isUa ? 'Оформити в додатку Сільпо 📱' : 'Checkout in Silpo App 📱'}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          openStoreBrandedAppOrWeb({
                            store: 'silpo',
                            webUrl: silpoCheckoutWebLink || 'https://silpo.ua',
                            action: 'checkout',
                            forceWeb: true
                          });
                        }}
                        className="w-full py-2.5 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold flex items-center justify-center gap-2 transition-all text-xs cursor-pointer"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        {isUa ? 'Оформити на сайті silpo.ua 🌐' : 'Checkout on Website 🌐'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleOpenStoreAppOrWeb(orderSummary?.receiptUrl, 'checkout')}
                        className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 transition-all text-xs uppercase tracking-wider cursor-pointer shadow-md shadow-emerald-500/20"
                      >
                        <Smartphone className="w-4 h-4" />
                        {isUa ? `Оформити в додатку ${storeName} 📱` : `Checkout in ${storeName} App 📱`}
                      </button>

                      <a
                        href={orderSummary?.receiptUrl || getStoreCheckoutUrl(rawStore)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2.5 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold flex items-center justify-center gap-2 transition-all text-xs cursor-pointer"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        {isUa ? `Відкрити сайт ${storeName} 🌐` : `Open ${storeName} Website 🌐`}
                      </a>
                    </>
                  )}
                  <button
                    onClick={handleCopyList}
                    className="w-full py-2.5 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {isUa ? 'Скопіювати чек замовлення 📋' : 'Copy Order Receipt 📋'}
                  </button>
                </div>
              </div>
            )}

            {step === 'paying' && (
              <div className="text-center py-10 space-y-5">
                <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-4 border-zinc-200 dark:border-zinc-800"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
                  <ShieldCheck className="w-8 h-8 text-emerald-500 animate-pulse" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">
                    {isUa ? 'Підключення до платіжного шлюзу...' : 'Connecting to Payment Gateway...'}
                  </h3>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium">
                    {storeName} Direct Checkout Handshake
                  </p>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-900/60 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 text-xs text-zinc-600 dark:text-zinc-400 space-y-2 max-w-sm mx-auto">
                  <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 font-semibold text-[11px]">
                    <span className="flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" />
                      256-bit SSL Secure Channel
                    </span>
                    <span>PCI-DSS</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                    {isUa 
                      ? "Формуємо безпечну платіжну сесію та перенаправляємо на офіційний шлюз Сільпо." 
                      : "Establishing secure transaction session and opening official store payment portal."}
                  </p>
                </div>
              </div>
            )}

            {step === 'order' && (
              /* DEDICATED ORDER DOUBLE-CHECK PAGE */
              <div className="space-y-4">
                {/* Order Meta Bar */}
                <div className="bg-emerald-500/10 dark:bg-emerald-500/15 p-3.5 rounded-2xl border border-emerald-500/20 dark:border-emerald-500/30 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                    <span className="font-bold text-zinc-800 dark:text-zinc-200">
                      {isUa ? 'Замовлення:' : 'Order Ref:'}
                    </span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">{orderReferenceId}</span>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10px] uppercase font-bold">
                    {isUa ? 'Готово до оплати' : 'Ready to Pay'}
                  </span>
                </div>

                {/* Section 1: Delivery & Contact Verification */}
                <div className="bg-zinc-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-3">
                  <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 pb-2">
                    <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide flex items-center gap-1.5">
                      {isPickup ? <Store className="w-3.5 h-3.5 text-emerald-500" /> : <Truck className="w-3.5 h-3.5 text-emerald-500" />}
                      {isPickup 
                        ? (isUa ? '1. Самовивіз та контактні дані' : '1. Pickup & Contact Info') 
                        : (isUa ? '1. Доставка та контактні дані' : '1. Delivery & Contact Info')}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {storeThreshold.pickupAvailable && (
                        <div className="flex gap-1 bg-white dark:bg-zinc-950 p-0.5 rounded-lg border border-zinc-200 dark:border-zinc-800">
                          <button
                            type="button"
                            onClick={() => setFulfillmentMethod('delivery')}
                            className={cn(
                              "px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer flex items-center gap-1",
                              !isPickup ? "bg-emerald-500 text-white shadow-xs" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                            )}
                          >
                            <Truck className="w-2.5 h-2.5" />
                            <span>{isUa ? 'Доставка' : 'Delivery'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFulfillmentMethod('pickup')}
                            className={cn(
                              "px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer flex items-center gap-1",
                              isPickup ? "bg-emerald-500 text-white shadow-xs" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                            )}
                          >
                            <Store className="w-2.5 h-2.5" />
                            <span>{isUa ? 'Самовивіз' : 'Pickup'}</span>
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          if (isEditingAddress) {
                            try {
                              const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
                              const key = rawStore.toLowerCase();
                              stores[key] = {
                                ...(stores[key] || {}),
                                address: deliveryAddress,
                                notes: deliveryNotes,
                                phone: recipientPhone,
                                name: recipientName,
                                connected: true
                              };
                              localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
                              toast.success(isUa ? 'Контактні дані збережено' : 'Contact details saved');
                            } catch (e) {}
                          }
                          setIsEditingAddress(!isEditingAddress);
                        }}
                        className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer ml-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        {isEditingAddress ? (isUa ? 'Зберегти' : 'Done') : (isUa ? 'Редагувати' : 'Edit')}
                      </button>
                    </div>
                  </div>

                  {/* Delivery threshold alert if below minimum in order step */}
                  {isBelowMinDelivery && (
                    <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-300 font-bold text-[11px]">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        <span>
                          {isUa 
                            ? `Мінімум доставки Сільпо: ₴${storeThreshold.minDeliverySum} (бракує ${currencySymbol}${deficitToMin})` 
                            : `Delivery minimum: ${currencySymbol}${storeThreshold.minDeliverySum} (need +${currencySymbol}${deficitToMin})`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleAutoTopUp}
                        className="px-2 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-[10px] flex items-center gap-1 cursor-pointer"
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>{isUa ? '⚡ Доповнити' : '⚡ Top Up'}</span>
                      </button>
                    </div>
                  )}

                  {isEditingAddress ? (
                    <div className="space-y-2 text-xs">
                      {availableAddresses.length > 0 && !isPickup && (
                        <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-1.5 mb-2">
                          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 block">
                            {isUa ? '📍 Адреси з вашого додатку Сільпо:' : '📍 Saved addresses from your Silpo app:'}
                          </span>
                          <div className="space-y-1">
                            {availableAddresses.map(addr => (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => {
                                  setDeliveryAddress(addr.streetLine);
                                  if (addr.comment) setDeliveryNotes(addr.comment);
                                  toast.info(isUa ? `Обрано: ${addr.streetLine}` : `Selected: ${addr.streetLine}`);
                                }}
                                className={cn(
                                  "w-full text-left p-2 rounded-lg text-[11px] border transition-all cursor-pointer flex flex-col gap-0.5",
                                  deliveryAddress === addr.streetLine
                                    ? "bg-emerald-500 text-white border-emerald-500 font-bold shadow-xs"
                                    : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-800 dark:text-zinc-200 hover:border-emerald-500/50"
                                )}
                              >
                                <span className="font-semibold">{addr.streetLine} {addr.city ? `(${addr.city})` : ''}</span>
                                {addr.comment && (
                                  <span className={cn("text-[10px] truncate", deliveryAddress === addr.streetLine ? "text-emerald-100" : "text-zinc-500 dark:text-zinc-400")}>
                                    {addr.comment}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block mb-1">
                          {isPickup 
                            ? (isUa ? 'Пункт видачі / Адреса супермаркету:' : 'Pickup Location / Store Address:')
                            : (isUa ? 'Адреса доставки:' : 'Delivery Address:')}
                        </label>
                        <input
                          type="text"
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          placeholder={isPickup ? (isUa ? "Супермаркет Сільпо, вул. Хрещатик, 26" : "Store Pickup Hub") : (isUa ? "вул. Гурія Бухала, 11, кв. 40" : "124 Main St, Apt 4B")}
                          className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 rounded-xl p-2.5 text-zinc-900 dark:text-zinc-100 text-xs focus:border-emerald-500 outline-none placeholder:italic"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block mb-1">
                            {isUa ? 'Телефон:' : 'Phone:'}
                          </label>
                          <input
                            type="text"
                            value={recipientPhone}
                            onChange={(e) => setRecipientPhone(e.target.value)}
                            placeholder="+380 63 498 55 59"
                            className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 rounded-xl p-2.5 text-zinc-900 dark:text-zinc-100 text-xs focus:border-emerald-500 outline-none placeholder:italic"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block mb-1">
                            {isUa ? 'Отримувач:' : 'Name:'}
                          </label>
                          <input
                            type="text"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            placeholder={isUa ? "Ваше ім'я" : "Your Name"}
                            className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 rounded-xl p-2.5 text-zinc-900 dark:text-zinc-100 text-xs focus:border-emerald-500 outline-none placeholder:italic"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 block mb-1">
                          {isPickup ? (isUa ? 'Коментар до видачі:' : 'Pickup Notes:') : (isUa ? 'Коментар для курʼєра:' : 'Courier Notes:')}
                        </label>
                        <input
                          type="text"
                          value={deliveryNotes}
                          onChange={(e) => setDeliveryNotes(e.target.value)}
                          placeholder={isPickup ? (isUa ? "Заберу після роботи" : "Will pick up after 6pm") : (isUa ? "Подзвоніть біля під'їзду, я вийду відкрию" : "Leave at door")}
                          className="w-full bg-white dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 rounded-xl p-2.5 text-zinc-900 dark:text-zinc-100 text-xs focus:border-emerald-500 outline-none placeholder:italic"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-xs">
                      <div className="flex items-start gap-2.5">
                        <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-zinc-900 dark:text-zinc-100">{deliveryAddress || (isUa ? "Адресу не вказано" : "No address specified")}</p>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{deliveryNotes || (isUa ? "Без додаткового коментаря" : "No delivery notes")}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-200 dark:border-zinc-800 text-[11px]">
                        <div className="flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300">
                          <Clock className="w-3.5 h-3.5 text-emerald-500" />
                          <span>{isExpressSelected && silpoExpress ? (isUa ? `⚡ Експрес (~${silpoExpress.promiseMinutes} хв)` : `⚡ Express (~${silpoExpress.promiseMinutes}m)`) : deliverySlot}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300">
                          <Phone className="w-3.5 h-3.5 text-emerald-500" />
                          <span>{recipientPhone} {recipientName ? `(${recipientName})` : ''}</span>
                        </div>
                      </div>

                      {rawStore.toLowerCase() === 'silpo' && silpoExpress?.isAvailable && (
                        <div className="mt-2 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-emerald-500 shrink-0" />
                            <div>
                              <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[11px] block">
                                {isUa ? '⚡ Доступна експрес-доставка «Сільпо»!' : '⚡ Silpo Express Delivery Available!'}
                              </span>
                              <span className="text-[10px] text-zinc-500 dark:text-zinc-400">
                                {isUa 
                                  ? `Доставка курʼєром за ~${silpoExpress.promiseMinutes} хв (+₴${silpoExpress.price})` 
                                  : `Courier delivery in ~${silpoExpress.promiseMinutes} min (+₴${silpoExpress.price})`}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsExpressSelected(!isExpressSelected)}
                            className={cn(
                              "px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer",
                              isExpressSelected 
                                ? "bg-emerald-500 text-white shadow-xs" 
                                : "bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:border-emerald-500"
                            )}
                          >
                            {isExpressSelected ? (isUa ? '✓ Обрано' : '✓ Active') : (isUa ? 'Обрати' : 'Select')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Silpo Loyalty / Vlasna Kartka Card & Store Account Login */}
                  {rawStore.toLowerCase() === 'silpo' && (
                    <div className="pt-2.5 border-t border-zinc-200 dark:border-zinc-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-amber-500 shrink-0" />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-[11px] font-bold text-zinc-900 dark:text-zinc-100">«Власна Картка» Сільпо</p>
                              {boundAccountData?.connected && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                                  <Check className="w-2.5 h-2.5" /> {boundAccountData.cardId || boundAccountData.phone || 'Підключено'}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                              {boundAccountData?.connected 
                                ? (isUa 
                                    ? `${loyaltyBonusBalance.toFixed(2)} балабонусів доступно (знижка ₴${bonusDiscount.toFixed(2)})` 
                                    : `${loyaltyBonusBalance.toFixed(2)} loyalty bonuses available (discount ₴${bonusDiscount.toFixed(2)})`)
                                : (isUa ? 'Увійдіть для нарахування персональних балабонусів' : 'Sign in to sync loyalty points & discounts')}
                            </p>
                          </div>
                        </div>

                        {boundAccountData?.connected ? (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={applyBonuses}
                                onChange={(e) => setApplyBonuses(e.target.checked)}
                                className="accent-emerald-500 w-4 h-4 rounded cursor-pointer"
                              />
                              <span className="text-[10px] font-semibold text-zinc-600 dark:text-zinc-300">
                                {isUa ? 'Застосувати' : 'Apply'}
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setIsStoreLoginModalOpen(true)}
                              className="text-[10px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 underline cursor-pointer"
                            >
                              {isUa ? 'Змінити' : 'Change'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsStoreLoginModalOpen(true)}
                            className="py-1 px-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-[10px] border border-emerald-500/30 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <Smartphone className="w-3 h-3" />
                            {isUa ? 'Увійти' : 'Sign In'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 2: Items Order Review with +/- Qty & Remove */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide flex items-center gap-1.5">
                      <ShoppingCart className="w-3.5 h-3.5 text-emerald-500" />
                      {isUa ? `2. Огляд товарів у кошику (${displayedItems.length})` : `2. Order Review Items (${displayedItems.length})`}
                    </span>
                    <span className="text-[10px] text-zinc-400">
                      {isUa ? 'Натисніть + або - для зміни' : 'Adjust qty with + or -'}
                    </span>
                  </div>

                  <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                    {displayedItems.map((item) => {
                      const measurement = getItemMeasurement(item, isUa);
                      return (
                        <div
                          key={item.itemId}
                          className="flex items-center justify-between bg-white dark:bg-zinc-900/60 p-3 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors gap-2 text-xs"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <span className="text-lg select-none shrink-0">{item.icon || '🛒'}</span>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">{item.name}</p>
                              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 dark:text-zinc-400">
                                <span className="text-emerald-600 dark:text-emerald-400 font-medium">+{item.macros.protein * item.quantity}g {isUa ? 'білка' : 'protein'}</span>
                                <span>•</span>
                                <span className="font-bold text-zinc-800 dark:text-zinc-200 px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800">
                                  {measurement.label}
                                </span>
                                <span>•</span>
                                <span>{currencySymbol}{item.price.toFixed(2)}{measurement.unitRate}</span>
                              </div>
                            </div>
                          </div>

                          {/* Interactive Stepper */}
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-center bg-zinc-100 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg p-0.5">
                              <button
                                onClick={() => handleUpdateQty(item.itemId, -1)}
                                className="w-6 h-6 flex items-center justify-center text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors cursor-pointer"
                                title={isUa ? 'Зменшити' : 'Decrease'}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="min-w-7 px-1 text-center font-bold text-zinc-900 dark:text-zinc-100 text-xs">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQty(item.itemId, 1)}
                                className="w-6 h-6 flex items-center justify-center text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors cursor-pointer"
                                title={isUa ? 'Збільшити' : 'Increase'}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>

                            <span className="text-zinc-900 dark:text-zinc-100 font-bold w-16 text-right">
                              {currencySymbol}{(item.price * item.quantity).toFixed(2)}
                            </span>

                            <button
                              onClick={() => handleRemoveItem(item.itemId)}
                              className="p-1 text-zinc-400 hover:text-rose-500 transition-colors cursor-pointer"
                              title={isUa ? 'Видалити' : 'Remove'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SafeChoice Dietary Certification Banner */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 p-3.5 rounded-2xl flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300">
                      {isUa ? '100% Верифікація SafeChoice' : '100% SafeChoice Compliance'}
                    </p>
                    <p className="text-[10px] text-zinc-600 dark:text-zinc-400 leading-tight">
                      {isUa 
                        ? `Жодних заборонених E-добавок. Загалом: ${totalProtein}g білка • ~${totalCalories} ккал.` 
                        : `No banned additives or allergens. Total: ${totalProtein}g protein • ~${totalCalories} kcal.`}
                    </p>
                  </div>
                </div>

                {/* Section 3: Payment Method Selection */}
                <div className="bg-zinc-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 space-y-2.5">
                  <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
                    {isUa ? '3. Спосіб оплати' : '3. Payment Method'}
                  </span>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('apple_pay')}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between",
                        paymentMethod === 'apple_pay'
                          ? "bg-emerald-500/10 border-emerald-500 text-emerald-800 dark:text-emerald-200 shadow-xs ring-1 ring-emerald-500/30"
                          : "bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">Apple / Google Pay</span>
                        {paymentMethod === 'apple_pay' && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      <span className="text-[10px] text-zinc-400">
                        {isUa ? '1 клік' : 'Instant'}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between",
                        paymentMethod === 'card'
                          ? "bg-emerald-500/10 border-emerald-500 text-emerald-800 dark:text-emerald-200 shadow-xs ring-1 ring-emerald-500/30"
                          : "bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">{isUa ? 'Картка' : 'Bank Card'}</span>
                        {paymentMethod === 'card' && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      <span className="text-[10px] text-zinc-400">
                        Visa / MC
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('store_direct')}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between",
                        paymentMethod === 'store_direct'
                          ? "bg-emerald-500/10 border-emerald-500 text-emerald-800 dark:text-emerald-200 shadow-xs ring-1 ring-emerald-500/30"
                          : "bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs">{rawStore.toLowerCase() === 'silpo' ? 'silpo.ua' : storeName}</span>
                        {paymentMethod === 'store_direct' && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      <span className="text-[10px] text-zinc-400">
                        {isUa ? 'Каса / Додаток' : 'Store Portal'}
                      </span>
                    </button>
                  </div>

                  {isSilpo && (
                    <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-2.5 text-xs text-emerald-800 dark:text-emerald-200">
                      <Smartphone className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-[11px] text-emerald-700 dark:text-emerald-300">
                            {isUa ? 'Оплата у додатку Сільпо 📱' : 'Payment in Silpo App 📱'}
                          </p>
                          <button
                            type="button"
                            onClick={() => setIsStoreLoginModalOpen(true)}
                            className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
                          >
                            {boundAccountData?.phone || boundAccountData?.cardId ? (isUa ? 'Змінити акаунт' : 'Change Account') : (isUa ? 'Звʼязати номер 📱' : 'Link Phone 📱')}
                          </button>
                        </div>
                        <p className="text-[10px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
                          {isUa 
                            ? `Замовлення буде відкрито напряму у мобільному застосунку Сільпо${boundAccountData?.phone ? ` (${boundAccountData.phone})` : ''} для швидкої оплати карткою або Apple Pay / Google Pay.`
                            : `Order will open directly in your Silpo mobile app${boundAccountData?.phone ? ` (${boundAccountData.phone})` : ''} for quick Apple Pay / Google Pay / card payment.`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 4: Cost Breakdown */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 space-y-2 text-xs">
                  <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                    <span>{isUa ? 'Сума товарів:' : 'Subtotal:'}</span>
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                    <span>{isPickup ? (isUa ? 'Самовивіз з магазину:' : 'Store Pickup:') : (isUa ? 'Доставка курʼєром:' : 'Courier Delivery:')}</span>
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">{deliveryFee === 0 ? (isUa ? 'Безкоштовно (0 ₴)' : 'Free') : `${currencySymbol}${deliveryFee.toFixed(2)}`}</span>
                  </div>
                  {bonusDiscount > 0 && (
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                      <span>{isUa ? 'Балабонуси Сільпо:' : 'Loyalty Bonuses:'}</span>
                      <span>-{currencySymbol}{bonusDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 flex justify-between text-sm font-bold text-zinc-900 dark:text-zinc-50">
                    <span>{isUa ? 'Разом до сплати:' : 'Total Payable:'}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 text-base font-black">{currencySymbol}{totalCost.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {step === 'review' && (
              /* INITIAL CART REVIEW STEP */
              <>
                {/* Dynamic Copy Engine banner */}
                <div className="bg-zinc-50 dark:bg-zinc-900/60 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed font-sans space-y-2">
                  <p>{copyMessage}</p>
                  {boundAccountData && (
                    <div className="flex items-center gap-2 pt-2 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        {boundAccountData.type === 'app' || boundAccountData.appBound
                          ? (isUa 
                              ? `📱 Зв'язано додаток: ${boundAccountData.phone || boundAccountData.cardId || storeName}` 
                              : `📱 Linked App: ${boundAccountData.phone || boundAccountData.cardId || storeName}`)
                          : (isUa 
                              ? `🌐 Веб-акаунт: ${boundAccountData.email || 'Google Auth'}` 
                              : `🌐 Web Account: ${boundAccountData.email || 'Google Auth'}`)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Fulfillment / Threshold Status Card in Review Step */}
                <div className={cn(
                  "p-3.5 rounded-2xl border transition-all space-y-2.5 text-xs",
                  isBelowMinDelivery 
                    ? "bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-200" 
                    : "bg-zinc-50 dark:bg-zinc-900/60 border-zinc-200 dark:border-zinc-800 text-zinc-800 dark:text-zinc-200"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-bold">
                      {isBelowMinDelivery ? (
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      )}
                      <span className={isBelowMinDelivery ? "text-amber-800 dark:text-amber-300 font-bold" : "text-zinc-800 dark:text-zinc-200"}>
                        {isPickup
                          ? (isUa ? '🏬 Самовивіз: мінімальна сума 0 ₴ (безкоштовно)' : '🏬 Free Pickup: No order minimum')
                          : isBelowMinDelivery
                            ? (isUa ? `До мінімуму доставки бракує ${currencySymbol}${deficitToMin}` : `Need +${currencySymbol}${deficitToMin} for delivery min`)
                            : (isUa ? `Мінімум доставки (${currencySymbol}${storeThreshold.minDeliverySum}) виконано` : `Delivery minimum (${currencySymbol}${storeThreshold.minDeliverySum}) met`)}
                      </span>
                    </div>
                    {storeThreshold.pickupAvailable && (
                      <button
                        type="button"
                        onClick={() => {
                          const nextMethod = isPickup ? 'delivery' : 'pickup';
                          setFulfillmentMethod(nextMethod);
                          toast.info(nextMethod === 'pickup' 
                            ? (isUa ? 'Перемкнуто на Самовивіз (без мінімальної суми)' : 'Switched to Pickup (0 min)') 
                            : (isUa ? 'Перемкнуто на Доставку курʼєром' : 'Switched to Delivery'));
                        }}
                        className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer font-bold shrink-0 ml-2"
                      >
                        {isPickup ? (isUa ? '🚚 Доставка' : '🚚 Delivery') : (isUa ? '🏬 Самовивіз (безкоштовно)' : '🏬 Free Pickup')}
                      </button>
                    )}
                  </div>

                  {!isPickup && (
                    <div className="w-full h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all duration-500 rounded-full",
                          isBelowMinDelivery ? "bg-amber-500" : "bg-emerald-500"
                        )}
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  )}

                  {isBelowMinDelivery && (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleAutoTopUp}
                        className="flex-1 py-1.5 px-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-[11px] flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs active:scale-95"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{isUa ? `⚡ Доповнити дефіцитами (+${currencySymbol}${deficitToMin})` : `⚡ Auto-Top Up (+${currencySymbol}${deficitToMin})`}</span>
                      </button>
                      {storeThreshold.pickupAvailable && (
                        <button
                          type="button"
                          onClick={() => {
                            setFulfillmentMethod('pickup');
                            toast.info(isUa ? 'Перемкнуто на Самовивіз (без мінімальної суми)' : 'Switched to Pickup (no order minimum)');
                          }}
                          className="py-1.5 px-2.5 rounded-xl bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 font-bold text-[11px] transition-all cursor-pointer whitespace-nowrap"
                        >
                          {isUa ? '🏬 Самовивіз (безкоштовно)' : '🏬 Free Pickup'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Itemized Cart Breakdown */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide">
                      {isUa ? 'Сформований Кошик Nutri-Agent' : 'Allocated Cart Items'}
                    </span>
                    <button
                      onClick={handleCopyList}
                      className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer font-medium"
                    >
                      <Copy className="w-3 h-3" />
                      {isUa ? 'Скопіювати' : 'Copy'}
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {displayedItems.map((item) => {
                      const measurement = getItemMeasurement(item, isUa);
                      return (
                        <div key={item.itemId} className="flex flex-col gap-1.5 bg-zinc-50 dark:bg-zinc-900/40 p-3.5 rounded-2xl border border-zinc-200 dark:border-zinc-800 text-xs hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                              {item.icon && <span className="text-lg select-none">{item.icon}</span>}
                              <div>
                                <p className="font-semibold text-zinc-900 dark:text-zinc-100">{item.name}</p>
                                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                                  <span>+{item.macros.protein * item.quantity}g {isUa ? 'білка' : 'protein'}</span>
                                  <span>•</span>
                                  <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">
                                    {measurement.label}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className="text-zinc-900 dark:text-zinc-100 font-bold">
                              {currencySymbol}{(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        {item.productUrl && (
                          <button
                            type="button"
                            onClick={() => handleOpenStoreAppOrWeb(item.productUrl, 'product', item.name)}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:underline pt-1 border-t border-zinc-200 dark:border-zinc-800/60 cursor-pointer text-left"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {rawStore.toLowerCase() === 'silpo' 
                              ? (isUa ? 'Переглянути товар у Сільпо ↗' : 'View product in Silpo ↗')
                              : (isUa ? `Пошук товару на ${storeName} ↗` : `Find on ${storeName} ↗`)}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>

                {/* Total vs Budget */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 space-y-2 text-xs">
                  <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                    <span>{isUa ? 'Сума товарів:' : 'Subtotal:'}</span>
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                    <span>{isPickup ? (isUa ? 'Самовивіз з магазину:' : 'Store Pickup:') : (isUa ? 'Орієнтовна доставка:' : 'Estimated Delivery:')}</span>
                    <span className="font-semibold text-zinc-800 dark:text-zinc-200">{deliveryFee === 0 ? (isUa ? 'Безкоштовно (0 ₴)' : 'Free') : `${currencySymbol}${deliveryFee.toFixed(2)}`}</span>
                  </div>
                  <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 flex justify-between text-sm font-bold text-zinc-900 dark:text-zinc-50">
                    <span>{isUa ? 'Разом (Бюджет:' : 'Total (Budget:'} {currencySymbol}{config.budgetLimitUSD.toFixed(2)}):</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">{currencySymbol}{totalCost.toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer Action Buttons */}
          <div className="p-5 pt-3.5 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col gap-2 shrink-0">
            {step === 'confirmed' ? (
              <button
                onClick={onClose}
                className="w-full py-3.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-all shadow-md shadow-emerald-500/20 cursor-pointer text-xs uppercase tracking-wider"
              >
                {isUa ? 'Закрити' : 'Close'}
              </button>
            ) : step === 'paying' ? (
              <button
                disabled
                className="w-full py-3.5 px-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-400 font-bold flex items-center justify-center gap-2 text-xs cursor-not-allowed"
              >
                <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                {isSilpo 
                  ? (isUa ? 'Синхронізуємо кошик Сільпо та готуємо оплату...' : 'Syncing Silpo cart & preparing checkout...')
                  : (isUa ? 'Безпечне зʼєднання із шлюзом...' : 'Connecting to secure gateway...')}
              </button>
            ) : step === 'order' ? (
              /* ORDER PAGE ACTIONS: PROCEED TO PAYMENT */
              <>
                <button
                  onClick={handleProceedToPayment}
                  className={cn(
                    "w-full py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 cursor-pointer",
                    "bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-all shadow-md shadow-emerald-500/20 text-xs sm:text-sm uppercase tracking-wider"
                  )}
                >
                  <Lock className="w-4 h-4" />
                  {isSilpo 
                    ? (isUa 
                        ? `Оплатити в додатку Сільпо (${currencySymbol}${totalCost.toFixed(2)}) 📱🔒` 
                        : `Pay in Silpo App (${currencySymbol}${totalCost.toFixed(2)}) 📱🔒`)
                    : (isUa 
                        ? `Перейти до оплати (${currencySymbol}${totalCost.toFixed(2)}) 🔒` 
                        : `Proceed to Payment (${currencySymbol}${totalCost.toFixed(2)}) 🔒`)}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setStep('review')}
                    className="py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    {isUa ? 'Назад до кошика' : 'Back to Cart'}
                  </button>
                  <button
                    onClick={handleCopyList}
                    className="py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {isUa ? 'Скопіювати чек' : 'Copy Summary'}
                  </button>
                </div>
              </>
            ) : (
              /* STEP === 'REVIEW': PRIMARY BUTTON TAKES USER TO ORDER PAGE */
              <>
                <button
                  onClick={() => setStep('order')}
                  className={cn(
                    "w-full py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 cursor-pointer",
                    "bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-all shadow-md shadow-emerald-500/20 text-xs sm:text-sm uppercase tracking-wider"
                  )}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {isSilpo
                    ? (isUa ? 'Перейти до оформлення в Сільпо ↗' : 'Proceed to Silpo Checkout ↗')
                    : (isUa ? `Оформити замовлення на ${storeName} ↗` : `Proceed to ${storeName} ↗`)}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={onClose}
                    className="py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-all cursor-pointer"
                  >
                    {isUa ? 'Скасувати' : 'Cancel Run'}
                  </button>
                  <button
                    onClick={handleCopyList}
                    className="py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-900 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200 dark:border-zinc-800 text-xs font-semibold text-zinc-700 dark:text-zinc-300 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {isUa ? 'Скопіювати список' : 'Copy List'}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>

  <StoreLoginModal
    isOpen={isStoreLoginModalOpen}
    onClose={() => setIsStoreLoginModalOpen(false)}
    targetStore={rawStore.toLowerCase()}
    lang={lang}
    theme={theme}
    onSuccess={() => {
      setStoreLoginSuccessCount(c => c + 1);
      setIsStoreLoginModalOpen(false);
      toast.success(
        isUa 
          ? 'Акаунт Сільпо успішно оновлено! 📱✨' 
          : 'Silpo account successfully updated! 📱✨'
      );
    }}
  />
  </>
  );
};
